import React, { useEffect, useRef, useState } from "react";
import ActiveDeliveriesModal from "./ActiveDeliveriesModal";
import SendNowModal from "./SendNowModal";
import { ConfirmModal } from "../ConfirmModal";
import { useDeliverStory } from "../../../hooks/useDeliverStory";
import { useDeliveryRules } from "../../../hooks/useDeliveryRules";
import { useArchitecture } from "../../../hooks/useArchitecture";
import { useExportPresentationThemes } from "../../../hooks/useExportPresentationThemes";
import { useDeliveryAttachmentPreview } from "../../../hooks/useDeliveryAttachmentPreview";
import { DELIVER_COPY } from "./deliverStory.copy";
import { resolveDeliveryTheme } from "./deliveryTheme";
import { DEFAULT_MAX_ALERT_CONDITIONS } from "../../../services/delivery/alertCondition.config";
import { useSchedulePreview } from "../../../hooks/useSchedulePreview";
import { deliverStorySchedule } from "../../../services/delivery/deliverStoryDistribution";
import { sendInstantLabel } from "../../../services/distributions/distributionTime";
import { isScheduleValid } from "../../../utils/scheduleValidation";

type ConfirmKind = "cancel" | "delete";

const CONFIRM_COPY: Record<ConfirmKind, { title: string; description: string; confirmLabel: string }> =
  DELIVER_COPY.confirm;

export interface DeliverStoryFlowProps {
  open: boolean;
  /** The view shown each time the flow opens: the composer (default) or the deliveries list. */
  initialView?: "create" | "list";
  onOpenChange: (open: boolean) => void;
  storyId?: string;
  storyTitle?: string;
  storySummary?: string;
  workspaceId?: string;
  onExportRequest?: (format: string) => Promise<File | null>;
  maxAlertConditions?: number;
}

const DeliverStoryFlow: React.FC<DeliverStoryFlowProps> = ({
  open,
  initialView = "create",
  onOpenChange,
  storyId,
  storyTitle,
  storySummary,
  workspaceId,
  onExportRequest,
  maxAlertConditions = DEFAULT_MAX_ALERT_CONDITIONS,
}) => {
  const architecture = useArchitecture();
  const newArchitecture = architecture.status === "ready" ? !architecture.oldArchitecture : null;
  const {
    view,
    form,
    openDropdown,
    scheduleErrors,
    scheduleTouched,
    canSubmit,
    metricOptions,
    currentMetricTextByMetric,
    lastSendError,
    isSending,
    closeFlow,
    goToCreate,
    backToList,
    startEdit,
    handleFormChange,
    handleAddDestination,
    handleRemoveDestination,
    handleReplaceDestinations,
    handleScheduleChange,
    handleToggleWeekday,
    handleAddCondition,
    handleUpdateCondition,
    handleRemoveCondition,
    handleConditionMatchModeChange,
    handleOpenDropdown,
    handleCloseDropdown,
    handleSend,
    handleTestDelivery,
  } = useDeliverStory({
    onOpenChange,
    storyId,
    storyTitle,
    storySummary,
    workspaceId,
    onExportRequest,
    maxAlertConditions,
    newArchitecture,
  });

  const {
    deliveries,
    isLoading,
    isError,
    error,
    pause,
    resume,
    cancel,
    remove,
    isActionPending,
    actionError,
    clearActionError,
  } = useDeliveryRules(storyId, open, newArchitecture);

  // Deliver opens straight on the composer (F18). The list stays reachable
  // from the composer footer; we only jump on the closed -> open edge.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current && view === "list" && initialView === "create") goToCreate();
    wasOpen.current = open;
  }, [open, view, goToCreate, initialView]);

  // Same theme list as the Export modal; fetched while the create view is up.
  const isCreateOpen = open && view === "create";
  const themes = useExportPresentationThemes(isCreateOpen);
  const themeKey = resolveDeliveryTheme(form.theme, themes.defaultThemeKey);
  // The preview card's attachment: the real deck in that theme and format.
  const attachmentPreview = useDeliveryAttachmentPreview({
    workspaceId,
    storyId,
    isOpen: isCreateOpen,
    form,
    themeKey,
    themesErrorMessage: themes.errorMessage,
  });

  // Cancel and Delete are both irreversible server-side, so both route
  // through the same confirm step rather than firing straight from the row
  // menu — Pause/Resume stay a single click since either is reversible.
  const [pendingConfirm, setPendingConfirm] = useState<{ kind: ConfirmKind; id: string } | null>(null);

  const handleConfirm = () => {
    if (!pendingConfirm) return;
    (pendingConfirm.kind === "cancel" ? cancel : remove)(pendingConfirm.id);
    setPendingConfirm(null);
  };

  const { schedule } = form;
  const scheduled = form.whenToSend === "schedule";
  // The new architecture asks Story Manager when the schedule first sends: its start date may be past by then.
  const asksNextSend = newArchitecture === true && scheduled && isCreateOpen && isScheduleValid(schedule);
  const { preview } = useSchedulePreview(storyId, asksNextSend ? deliverStorySchedule(schedule) : null, { count: 1 });
  const firstRun = asksNextSend ? preview?.runs[0] : undefined;
  const firstSend = firstRun ? sendInstantLabel(firstRun, preview?.timezone ?? schedule.timezone) : null;
  const nextSendText =
    newArchitecture === true
      ? firstSend && `${DELIVER_COPY.nextSendPrefix} ${firstSend.date}, ${firstSend.time} ${firstSend.zone}`
      : scheduled && schedule.startsOn && schedule.refreshHour !== null
        ? `${DELIVER_COPY.nextSendPrefix} ${schedule.startsOn} ${String(schedule.refreshHour).padStart(2, "0")}:00${
            schedule.timezone ? ` ${schedule.timezone}` : ""
          }`
        : null;

  return (
    <>
      <ActiveDeliveriesModal
        open={open && view === "list"}
        onClose={closeFlow}
        onAddNew={goToCreate}
        deliveries={deliveries}
        isLoading={isLoading}
        isError={isError}
        errorMessage={error}
        actionErrorMessage={actionError}
        onEdit={newArchitecture === null ? undefined : startEdit}
        onPause={pause}
        onResume={resume}
        onCancel={(id) => {
          clearActionError();
          setPendingConfirm({ kind: "cancel", id });
        }}
        onDelete={(id) => {
          clearActionError();
          setPendingConfirm({ kind: "delete", id });
        }}
      />
      <ConfirmModal
        open={pendingConfirm !== null}
        isLoading={isActionPending}
        title={pendingConfirm ? CONFIRM_COPY[pendingConfirm.kind].title : ""}
        description={pendingConfirm ? CONFIRM_COPY[pendingConfirm.kind].description : ""}
        confirmLabel={pendingConfirm ? CONFIRM_COPY[pendingConfirm.kind].confirmLabel : "Confirm"}
        confirmVariant="danger"
        onConfirm={handleConfirm}
        onCancel={() => setPendingConfirm(null)}
        zIndex={1600}
      />
      <SendNowModal
        open={open && view === "create"}
        onClose={closeFlow}
        onCancel={closeFlow}
        onShowDeliveries={backToList}
        nextSendText={nextSendText}
        onSend={() =>
          handleSend({ theme: themeKey, artifactId: attachmentPreview.artifactId })
        }
        onTestDelivery={handleTestDelivery}
        form={form}
        openDropdown={openDropdown}
        scheduleErrors={scheduleErrors}
        scheduleTouched={scheduleTouched}
        maxAlertConditions={maxAlertConditions}
        canSubmit={canSubmit}
        metricOptions={metricOptions}
        currentMetricTextByMetric={currentMetricTextByMetric}
        lastSendError={lastSendError}
        isSending={isSending}
        onFormChange={handleFormChange}
        onAddDestination={handleAddDestination}
        onRemoveDestination={handleRemoveDestination}
        onReplaceDestinations={handleReplaceDestinations}
        onScheduleChange={handleScheduleChange}
        onToggleWeekday={handleToggleWeekday}
        onAddCondition={handleAddCondition}
        onUpdateCondition={handleUpdateCondition}
        onRemoveCondition={handleRemoveCondition}
        onConditionMatchModeChange={handleConditionMatchModeChange}
        onOpenDropdown={handleOpenDropdown}
        onCloseDropdown={handleCloseDropdown}
        themeOptions={themes.options}
        themeValue={themeKey}
        isThemesLoading={themes.isLoading}
        themesErrorMessage={themes.errorMessage}
        // The choice is saved in a distribution, which only the new architecture's schedule and email Send now use.
        showAudience={
          newArchitecture === true && (form.whenToSend === "schedule" || (form.whenToSend === "send-now" && form.platform === "email"))
        }
        attachment={attachmentPreview.attachment}
      />
    </>
  );
};

export default DeliverStoryFlow;
